/* Quality Report (Texas QC) — a per-pallet PDF annex for the "Send
   Quality Report" shipper email (ShipmentRegistry.js). Same
   build-once / reuse pattern as protectionReport.js/
   paymentConfirmation.js: an HTML builder, a Blob wrapper
   (htmlToPdfBlob) shared by the download button and the auto-attached
   email copy, and a React preview modal.

   2026-09-14, Arturo: "quiero enviarlo por la app al shipper, estos no
   tienen nada que ver con los reportes del cliente, son internos
   entre GF y el shipper... quiero replicar 2 columnas del reporte una
   llamada quality y otra condition a nivel pallet... debo tambien
   generar un pdf informativo con el reporte de cada pallet... este
   debe mostrar las condiciones de la llegada a texas referentes a
   quality y condicion que yo mismo agregue en el embarque, aqui no
   aplica ni el bill ni el invoice, es especificamente el embarque".
   Unlike protectionReport.js (scoped to only protected pallets), this
   one lists EVERY real pallet in the shipment's manifest — "el
   reporte de cada pallet". */

import { useState } from "react";
import { createPortal } from "react-dom";
import { LOGO_B64 } from "./SalesReports";
import { htmlToPdfBlob, safeFilename } from "../pdfRenderer";

function escHtml(s) {
  return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

var GFI = {
  name: "Ground Fresh International, Inc.",
  lines: ["152 W Hoffman Ave Ste 5", "Lindenhurst, NY 11757", "USA"],
};

/* Same tags/colors as ShipmentRegistry.js's TEXAS_QC_TIERS/
   TEXAS_QC_COLORS, duplicated here per this codebase's per-file
   convention (calc/doc files don't import UI-only constants from
   registries) — keep these in sync if the tier set ever changes.
   2026-10-01 — Arturo: 2 kinds of Texas QC report share this file —
   GFI's own 2-column inspection (Quality/Condition) and ClariFresh's
   3-column one for Twin River (adds Severe Condition). Which one a
   given shipment used is shipment.texasQcMode ("internal" default, or
   "clarifresh") — that only controls the 3rd column; the tier set
   itself is shared/unified across both ("los conceptos de calidad
   pueden convivir juntos sin importar de quien es el reporte"). */
var LOSS_REASON_LABELS_ES = { makeup: "Make Up", repack: "Re-Pack", other: "Otra" };
/* 2026-10-16, Arturo: "los defectos en el pdf deben mostrarse en
   ingles tambien, los defectos siempre deben ser en ingles y debe
   mostrar los colores de severidad tambien" — defects stay English
   regardless of the rest of this report being Spanish (same exception
   CLAUDE.md already makes for generated documents in general), and
   each chip now carries the same severity coloring already used
   on-screen (ShipmentRegistry.js/BillRegistry.js's
   TOLERANCE_CATEGORY_COLORS/internalQualityDefectColor), not plain
   italic text. Same keys/order as ShipmentRegistry.js's
   TOLERANCE_DEFECTS, labels duplicated here per this codebase's
   per-file convention. */
var TOLERANCE_DEFECT_LABELS_EN = {
  decay: "Decay", mold: "Mold", insect: "Insect",
  leaky: "Leaky", shrivel: "Shrivel", soft: "Soft", brokenSkin: "Broken skin",
  attachedRemains: "Attached remains", floralRemains: "Floral remains", peduncle: "Peduncle", offColor: "Off color", berryDamage: "Berry damage",
  redBerries: "Red berries", greenBerries: "Green berries", scars: "Scars", dryCell: "Dry cell",
  /* Blackberry (2026-10-15, LOT 58069/QC 17470 + TR-SPEC-BLK-MX) — own
     keys, never shared with blueberry (see TOLERANCE_DEFECTS_BY_PRODUCT
     in ShipmentRegistry.js). */
  redCell: "Red cell", regression: "Regression", whiteCells: "White cells", redGreenDryTips: "Red/green or dry tips", attachedCore: "Attached core",
  /* GFI's own internal team (texasQcMode "internal"), 2026-10-15 — same
     real report "quality defects texas.pdf", LOT 58060 pallet 68299.
     redCells/dryCells/leaking/freezeDamage/softFruit are specific to
     this list; regression/whiteCells/shrivel/mold/decay already exist
     above (same labels). */
  redCells: "Red cells", dryCells: "Dry cells", leaking: "Leaking", freezeDamage: "Freeze damage", softFruit: "Soft fruit",
  /* 2026-10-21, Arturo: "Findings" — bugs/worms/insects found in the
     fruit, not a Condition/Quality tolerance defect. Same keys as
     ShipmentRegistry.js's QC_FINDINGS_OPTIONS. */
  bugs: "Bugs", worms: "Worms", insects: "Insects",
};
var TOLERANCE_CATEGORY_LABELS_EN = { severeCondition: "Severe Condition", condition: "Condition", quality: "Quality", findings: "Findings" };
/* Same category colors as ShipmentRegistry.js's TOLERANCE_CATEGORY_COLORS
   (ClariFresh's 3 real categories — red/amber/blue — plus Findings). */
var TOLERANCE_CATEGORY_COLORS = {
  severeCondition: { bg: "#FCE8E8", text: "#B91C1C" },
  condition: { bg: "#FDF1DE", text: "#B45309" },
  quality: { bg: "#E3ECF5", text: "#1F4E79" },
  /* 2026-10-21, Arturo: "cualquier finding encontrado es de severidad
     media alta" — same medium-high orange as ShipmentRegistry.js's
     TOLERANCE_CATEGORY_COLORS.findings. */
  findings: { bg: "#FDE4D0", text: "#C2410C" },
};
/* GFI Team (internal mode) per-defect severity — same as
   ShipmentRegistry.js's INTERNAL_SEVERE_DEFECT_KEYS/
   internalQualityDefectColor: everything that means the fruit is a
   total loss (decay/mold/insect/freezeDamage/regression) prints red,
   the rest (cosmetic/condition issues) prints amber. Internal-mode
   captures only ever live under "quality" (no 3-category split), so
   this only kicks in there. */
var INTERNAL_SEVERE_DEFECT_KEYS = { decay: true, mold: true, insect: true, freezeDamage: true, regression: true };
function internalQualityDefectColor(key) {
  return INTERNAL_SEVERE_DEFECT_KEYS[key] ? TOLERANCE_CATEGORY_COLORS.severeCondition : TOLERANCE_CATEGORY_COLORS.condition;
}
/* "regression" reads "Mechanical Damage" in internal-mode reports —
   matches the real GFI team report wording (same override as
   ShipmentRegistry.js/BillRegistry.js's internalDefectLabel). */
function defectLabelEn(e) {
  if (e.key === "__custom__") return e.custom || "Other";
  if (e.key === "regression") return "Mechanical Damage";
  return TOLERANCE_DEFECT_LABELS_EN[e.key] || e.key;
}
/* Colored chips per category/defect, English only, severity-colored —
   replaces the old plain-text italic summary. isClariFresh picks the
   coloring rule: ClariFresh's 3 categories get their own fixed color
   each; GFI's internal single "quality" bucket colors per-defect
   instead (same rule the on-screen Bill/Shipment views already use). */
/* 2026-10-27, Arturo: "los globos del pdf de reporte de calidad estan
   off, todos deben ser a un solo renglon y la burbuja del mismo
   tamaño" — a pallet with several defects spread across all 3
   categories (ex: LOT 58... pallet 008, 7 defects) used to overflow
   the fixed 700px page width at the old 8.5px/padding 6px sizing,
   wrapping the last chip alone onto an orphan second line. Tightened
   font-size/padding/margin to fit the realistic worst case on one
   line, and the whole row is forced white-space:nowrap so it never
   wraps again (every chip already carries the same padding/font, so
   they render at a uniform bubble size regardless of label length). */
function toleranceDefectsHtml(qcDefects, isClariFresh) {
  if (!qcDefects) return "";
  var chips = [];
  ["severeCondition", "condition", "quality", "findings"].forEach(function (cat) {
    var list = Array.isArray(qcDefects[cat]) ? qcDefects[cat] : [];
    if (list.length === 0) return;
    var catLabel = TOLERANCE_CATEGORY_LABELS_EN[cat];
    chips.push('<span style="font-size:7.2px;font-weight:700;color:#555;text-transform:uppercase;letter-spacing:0.2px;margin-right:2px">' + escHtml(catLabel) + ':</span>');
    list.forEach(function (e) {
      var label = defectLabelEn(e);
      var pct = (e.pct === 0 || e.pct) ? " " + e.pct + "%" : "";
      var c = cat === "findings" ? TOLERANCE_CATEGORY_COLORS.findings : (isClariFresh ? TOLERANCE_CATEGORY_COLORS[cat] : internalQualityDefectColor(e.key));
      chips.push('<span style="display:inline-block;padding:1px 4px;margin-right:3px;border-radius:5px;font-size:7.2px;font-weight:600;white-space:nowrap;background:' + c.bg + ';color:' + c.text + '">' + escHtml(label + pct) + '</span>');
    });
  });
  return '<div style="white-space:nowrap">' + chips.join("") + '</div>';
}
/* 2026-10-16, Arturo: "las columnas quality & condition en el pdf de
   calidad tambien debe ignorar el idioma y mostrarlos en ingles" — same
   English-always exception as the tolerance-sheet defects above, same
   literal labels ShipmentRegistry.js's texasQc* English translations
   already use for these exact tier keys. */
var TIER_LABELS_EN = { excellent: "Excellent", veryGood: "Very Good", goodQuality: "Good Quality", fairQuality: "Fair Quality", good: "Good", regular: "Regular", badQuality: "Bad Quality", poor: "Poor", poorQuality: "Poor Quality" };
var TIER_COLORS = {
  excellent: { bg: "#C8E6C9", text: "#1B5E20" },
  veryGood: { bg: "#E1BEE7", text: "#6A1B9A" },
  goodQuality: { bg: "#AED581", text: "#33691E" },
  /* 2026-10-13 — 5th Twin River/ClariFresh tier, same yellow as
     ShipmentRegistry.js's TEXAS_QC_COLORS.fairQuality — Arturo: "otro
     factor de calidad a twin river que es Fair Quality". */
  fairQuality: { bg: "#FFEB3B", text: "#5D4A00" },
  good: { bg: "#FFF59D", text: "#7A5C00" },
  regular: { bg: "#FFCDD2", text: "#B71C1C" },
  badQuality: { bg: "#C62828", text: "#FFFFFF" },
  poor: { bg: "#EF5350", text: "#FFFFFF" },
  /* 2026-10-02 — "poorQuality" is ClariFresh's own 4th tier, distinct
     from "poor" (GFI's own report) — Arturo: "poor y poor quality son
     distintos... uno se usa para mi reporte y el otro para clarifresh". */
  poorQuality: { bg: "#FFB74D", text: "#E65100" },
};
/* 2026-10-27, Arturo: "los globos del pdf de reporte de calidad estan
   off... la burbuja del mismo tamaño" — a fixed min-width (sized for
   the longest real label, "Fair Quality"/"Poor Quality") so every
   pill in the Quality/Condition/Severe Condition columns renders the
   same box regardless of label length, instead of shrink-wrapping to
   its own text and looking inconsistent pallet to pallet. */
function tierCellHtml(tier) {
  if (!tier || !TIER_COLORS[tier]) return '<span style="color:#999">—</span>';
  var c = TIER_COLORS[tier];
  return '<span style="display:inline-block;min-width:66px;padding:2px 10px;border-radius:10px;font-weight:700;white-space:nowrap;text-align:center;background:' + c.bg + ';color:' + c.text + '">' + (TIER_LABELS_EN[tier] || tier) + '</span>';
}

/* One row per REAL pallet (has a product and positive case count —
   same "real pallet" filter the rest of this app uses, e.g.
   ShipmentRegistry.js's own subtotal reducer) — every pallet, not
   just graded ones, so an ungraded pallet still shows on the report
   with "—" rather than silently disappearing. */
/* productFilter (optional) — array of "productBase||category" keys
   (see shipmentQualityProductGroups below). 2026-10-22, Arturo: Twin
   River (and his own quality team, for any client) send ONE quality
   report PER PRODUCT — even per product+category within the same lot
   ("si se maneja de un mismo producto 2 CAT... se harian reportes por
   separado") — so a shipment with Blackberry + Blueberry needs 2
   separate emails/PDFs, not one combined report covering both. When
   productFilter is omitted every real pallet is included (old
   behavior, unchanged for single-product shipments). */
export function buildQualityReportRows(shipment, productFilter) {
  var manifest = (shipment && shipment.palletManifest) || [];
  var allowed = Array.isArray(productFilter) ? productFilter : null;
  return manifest
    .filter(function (p) { return p && p.productBase && (parseInt(p.casesPallet, 10) || 0) > 0; })
    .filter(function (p) { return !allowed || allowed.indexOf(p.productBase + "||" + (p.category || "")) >= 0; })
    .map(function (p) {
      /* Same auto-generated format as protectionReport.js/
         ProductRegistry's own catalog SKUs: "[Product] [Units]/[Size]
         [Cat] [Label] Label". 2026-09-14 fix — Arturo: "en el reporte
         generado en pdf de qc reports de texas falto el Product
         Description en el detalle de los pallets" (the row builder
         never read unitsCase/size at all, so there was nothing to
         build a description from). */
      var desc = [p.productBase, (p.unitsCase || "") + "/" + (p.size || ""), p.category, p.labelInitials, "Label"].filter(Boolean).join(" ");
      return {
        palletNum: p.palletNum || "—",
        tagId: p.tagId || "—",
        productBase: p.productBase || "—",
        variety: p.variety || "—",
        category: p.category || "—",
        labelInitials: p.labelInitials || "—",
        productDescription: desc || "—",
        casesPallet: p.casesPallet || "—",
        texasQuality: p.texasQuality || "",
        texasCondition: p.texasCondition || "",
        texasSevereCondition: p.texasSevereCondition || "",
        lostCases: p.lostCases || "",
        lossReason: p.lossReason || "",
        lossReasonCustom: p.lossReasonCustom || "",
        qcDefects: p.qcDefects || null,
      };
    });
}

/* Distinct product+category combos actually present in this
   shipment's manifest, in first-seen order — "Blackberry OG" and
   "Blackberry CV" are 2 separate groups even though they're the same
   product, same as 2 different products. Used both to decide whether
   the product picker needs to show at all (1 group = nothing to pick,
   same as before this feature existed) and to build its options. */
export function shipmentQualityProductGroups(shipment) {
  var manifest = (shipment && shipment.palletManifest) || [];
  var groups = [];
  manifest.filter(function (p) { return p && p.productBase && (parseInt(p.casesPallet, 10) || 0) > 0; }).forEach(function (p) {
    var key = p.productBase + "||" + (p.category || "");
    if (!groups.some(function (g) { return g.key === key; })) {
      groups.push({ key: key, productBase: p.productBase, category: p.category || "", label: p.productBase + (p.category ? " " + p.category : "") });
    }
  });
  return groups;
}

/* shipment: the GFI shipment record. supplier: the shipper record.
   company: Company Profile (qualityReportDisclosureEs). Always
   Spanish — same override as the Protection Notice / this email's own
   body (shippers are Mexican, see CLAUDE.md's COMMUNICATION rules).
   productFilter — see buildQualityReportRows. */
export function buildQualityReportHTML(shipment, supplier, company, productFilter) {
  var rows = buildQualityReportRows(shipment, productFilter);
  var isClariFresh = shipment.texasQcMode === "clarifresh";
  var shipperName = (supplier && (supplier.fullName || supplier.shortName)) || shipment.shipperName || "—";
  var shipperAddrParts = supplier ? [supplier.address, [supplier.city, supplier.state].filter(Boolean).join(", "), supplier.country].filter(Boolean) : [];
  var totalCases = rows.reduce(function (s, r) { return s + (parseInt(r.casesPallet, 10) || 0); }, 0);
  var products = [...new Set(rows.map(function (r) { return r.productBase; }).filter(Boolean))].join(", ");
  /* 2026-10-01 — Arturo: the regular disclosure assumes the fruit
     travels on to a separate end client for their own receiving
     inspection — wrong for a ClariFresh report, which already IS that
     final check. Falls back to the regular one if the ClariFresh
     field was never configured in Company Profile, so this never
     prints blank. */
  var disclosureText = (company && (isClariFresh ? company.qualityReportDisclosureClarifreshEs : company.qualityReportDisclosureEs)) || (company && company.qualityReportDisclosureEs) || "";

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Quality Report — Lot ' + escHtml(shipment.lot || "") + '</title><style>';
  h += '@page{size:letter;margin:0.55in 0.6in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4;padding-top:14px}';
  h += '.page{max-width:700px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:3px solid #1B3A4B}';
  h += '.logo img{height:100px}';
  h += '.doc-title{font-size:22px;font-weight:300;color:#1B3A4B;letter-spacing:2px;text-align:right}';
  h += '.doc-sub{font-size:10px;color:#666;text-align:right;margin-top:4px}';
  h += '.parties{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin:14px 0}';
  h += '.party{padding:10px 14px;border:1px solid #e0e0e0;border-radius:6px;background:#fafbfc}';
  h += '.party .p-label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  h += '.party .p-name{font-size:13px;font-weight:700;color:#1B3A4B;margin-bottom:2px}';
  h += '.party .p-addr{font-size:9px;color:#666;line-height:1.6}';
  h += '.meta-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:0 0 12px;padding:12px 14px;background:#f7f8fa;border-radius:6px;border-left:4px solid #1B3A4B;border-right:4px solid #1B3A4B}';
  h += '.meta-grid .label{font-size:7.5px;text-transform:uppercase;letter-spacing:0.5px;color:#888;font-weight:700}';
  h += '.meta-grid .value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px}';
  h += '.notice-box{margin:0 0 14px;padding:12px 14px;background:#FFF8E1;border:1px solid #F5C244;border-radius:6px;font-size:10px;color:#6b4e00;white-space:pre-line}';
  h += '.notice-box b{display:block;font-size:10px;margin-bottom:4px;letter-spacing:0.5px}';
  h += '.loss-box{margin:0 0 14px;padding:12px 14px;background:#FFF5F5;border:1px solid #F5B5B5;border-left:4px solid #B91C1C;border-radius:6px;font-size:10px;color:#7F1D1D}';
  h += '.loss-box b{display:block;font-size:9px;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:6px;color:#B91C1C}';
  h += '.loss-row{margin:3px 0}';
  h += 'table{width:100%;border-collapse:collapse}';
  h += '.items th{padding:5px 6px;font-size:7.5px;text-transform:uppercase;letter-spacing:0.5px;color:#fff;background:#1B3A4B;font-weight:600}';
  h += '.items th:first-child{border-radius:4px 0 0 0}.items th:last-child{border-radius:0 4px 0 0}';
  h += '.items td{padding:4px 6px;border-bottom:1px solid #eee;font-size:9px;vertical-align:middle}';
  h += '.c{text-align:center}.mono{font-family:"Courier New",monospace}';
  h += '.totals{margin-top:10px;padding:8px 14px;background:#1B3A4B;color:#fff;border-radius:6px;display:flex;justify-content:space-between;font-size:10px;font-weight:600}';
  h += '.footer{margin-top:22px;padding-top:10px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  h += '</style></head><body><div class="page">';
  h += '<script>try{window.scrollTo(0,0);document.documentElement.scrollTop=0;document.body.scrollTop=0;}catch(e){}</script>';

  h += '<div class="header"><div class="logo"><img src="' + LOGO_B64 + '" /></div>';
  h += '<div><div class="doc-title">REPORTE DE CALIDAD</div>';
  h += '<div class="doc-sub">Texas QC Report' + (isClariFresh ? ' · Powered by ClariFresh' : '') + '</div></div></div>';

  h += '<div class="parties">';
  h += '<div class="party"><div class="p-label">De</div><div class="p-name">' + escHtml(GFI.name) + '</div>';
  h += '<div class="p-addr">' + GFI.lines.map(escHtml).join("<br/>") + '</div></div>';
  h += '<div class="party"><div class="p-label">Para (Shipper)</div><div class="p-name">' + escHtml(shipperName) + '</div>';
  if (shipperAddrParts.length) h += '<div class="p-addr">' + shipperAddrParts.map(escHtml).join("<br/>") + '</div>';
  h += '</div></div>';

  h += '<div class="meta-grid">';
  h += '<div><div class="label">Lote</div><div class="value">' + escHtml(shipment.lot || "—") + '</div></div>';
  h += '<div><div class="label">Proforma #</div><div class="value">' + escHtml(shipment.proforma || "—") + '</div></div>';
  h += '<div><div class="label">Factura Shipper #</div><div class="value">' + escHtml(shipment.shipperInvoice || "—") + '</div></div>';
  h += '<div><div class="label">Producto(s)</div><div class="value">' + escHtml(products || "—") + '</div></div>';
  h += '</div>';

  var lostRows = rows.filter(function (r) { return (parseInt(r.lostCases, 10) || 0) > 0; });
  if (lostRows.length > 0) {
    var totalLost = lostRows.reduce(function (s, r) { return s + (parseInt(r.lostCases, 10) || 0); }, 0);
    h += '<div class="loss-box"><b>CAJAS PERDIDAS EN RE-EMPAQUE (TEXAS)</b>';
    lostRows.forEach(function (r) {
      var reasonLabel = r.lossReason === "other" ? (r.lossReasonCustom || LOSS_REASON_LABELS_ES.other) : (LOSS_REASON_LABELS_ES[r.lossReason] || r.lossReason || "—");
      h += '<div class="loss-row">Pallet ' + escHtml(r.palletNum) + ' (Tag ' + escHtml(r.tagId) + '): ' + escHtml(r.lostCases) + ' cajas — ' + escHtml(reasonLabel) + '</div>';
    });
    h += '<div class="loss-row" style="font-weight:700;margin-top:6px">Total cajas perdidas: ' + totalLost + '</div>';
    h += '</div>';
  }

  if (disclosureText) {
    h += '<div class="notice-box"><b>AVISO — REPORTE DE CALIDAD</b>' + escHtml(disclosureText) + '</div>';
  }

  h += '<table class="items"><thead><tr>';
  h += '<th class="c">Pallet #</th>';
  h += '<th class="c">Warehouse Tag ID</th>';
  h += '<th>Product</th>';
  h += '<th class="c">Variety</th>';
  h += '<th class="c">Cat</th>';
  h += '<th class="c">Label</th>';
  h += '<th>Product Description</th>';
  h += '<th class="c">Cases</th>';
  h += '<th class="c">Quality</th>';
  h += '<th class="c">Condition</th>';
  if (isClariFresh) h += '<th class="c">Severe Condition</th>';
  h += '</tr></thead><tbody>';
  rows.forEach(function (r, i) {
    var bg = i % 2 === 1 ? ' style="background:#fafbfc"' : '';
    h += '<tr' + bg + '>';
    h += '<td class="c mono">' + escHtml(r.palletNum) + '</td>';
    h += '<td class="c mono">' + escHtml(r.tagId) + '</td>';
    h += '<td>' + escHtml(r.productBase) + '</td>';
    h += '<td class="c">' + escHtml(r.variety) + '</td>';
    h += '<td class="c">' + escHtml(r.category) + '</td>';
    h += '<td class="c">' + escHtml(r.labelInitials) + '</td>';
    h += '<td>' + escHtml(r.productDescription) + '</td>';
    h += '<td class="c">' + escHtml(r.casesPallet) + '</td>';
    h += '<td class="c">' + tierCellHtml(r.texasQuality) + '</td>';
    h += '<td class="c">' + tierCellHtml(r.texasCondition) + '</td>';
    if (isClariFresh) h += '<td class="c">' + tierCellHtml(r.texasSevereCondition) + '</td>';
    h += '</tr>';
    /* 2026-10-15, Arturo (GFI Team/internal mode): same per-pallet
       defect capture now also exists for non-ClariFresh shipments
       (Quality only — Condition stays a plain tier) — print it here
       too, not just the ClariFresh report. 2026-10-16: English labels
       + severity-colored chips, see toleranceDefectsHtml above. */
    var defectsHtml = toleranceDefectsHtml(r.qcDefects, isClariFresh);
    if (defectsHtml) {
      h += '<tr' + bg + '><td colspan="' + (isClariFresh ? 11 : 10) + '" style="padding:2px 6px 6px">' + defectsHtml + '</td></tr>';
    }
  });
  h += '</tbody></table>';

  h += '<div class="totals"><span>Pallets: ' + rows.length + '</span><span>Cajas: ' + totalCases + '</span></div>';

  h += '<div class="footer">Ground Fresh International, Inc. · 152 W Hoffman Ave Ste 5, Lindenhurst, NY 11757 · ground-fresh.com</div>';
  h += '</div></body></html>';
  return h;
}

export function buildQualityReportBlob(shipment, supplier, company, productFilter) {
  var html = buildQualityReportHTML(shipment, supplier, company, productFilter);
  return htmlToPdfBlob(html);
}

/* "Quality Report Lot {lot} {Shipper Name}" — parallel convention to
   protectionReportFilename (no per-PU/factura scope here since this
   is a whole-shipment document, not tied to any one invoice).
   2026-10-22 — productLabel (optional, e.g. "Blackberry OG") appended
   when this send was narrowed to one product/category group, so 2
   separate emails from the same lot never produce the same filename. */
export function qualityReportFilename(shipment, supplier, productLabel) {
  var shipperName = (supplier && (supplier.fullName || supplier.shortName)) || shipment.shipperName || "";
  var base = "Quality Report Lot " + (shipment.lot || "") + (productLabel ? " " + productLabel : "") + " " + shipperName;
  return safeFilename(base) + ".pdf";
}

export function QualityReportPreview({ shipment, supplier, company, productFilter, onClose }) {
  var html = buildQualityReportHTML(shipment, supplier, company, productFilter);
  var printDoc = function () {
    var w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    setTimeout(function () { w.print(); }, 400);
  };
  var [pdfBusy, setPdfBusy] = useState(false);
  var handleDownloadPdf = async function () {
    setPdfBusy(true);
    try {
      var blob = await buildQualityReportBlob(shipment, supplier, company);
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = qualityReportFilename(shipment, supplier);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("PDF error: " + (e.message || String(e)));
    } finally {
      setPdfBusy(false);
    }
  };
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 20002, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 60, overflowY: "auto" }}>
      <div style={{ width: 800, maxWidth: "97vw", background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden", marginBottom: 40 }}>
        <div style={{ padding: "10px 20px", background: "var(--color-bg-card-solid)", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Quality Report — Lot {shipment.lot}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={printDoc} style={{ padding: "6px 16px", borderRadius: 6, border: "none", background: "#1B3A4B", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Print / Save PDF</button>
            <button onClick={handleDownloadPdf} disabled={pdfBusy} style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid #1B3A4B", background: "transparent", color: "#1B3A4B", cursor: pdfBusy ? "wait" : "pointer", fontSize: 12, fontWeight: 600 }}>{pdfBusy ? "…" : "💾 Download PDF"}</button>
            <button onClick={onClose} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>Close</button>
          </div>
        </div>
        <div style={{ padding: 0, background: "#fff" }}>
          <iframe
            title="quality-report"
            srcDoc={html}
            onLoad={function (e) { try { var w = e.target.contentWindow; if (w) { w.scrollTo(0, 0); } } catch (err) {} }}
            style={{ width: "100%", height: "calc(100vh - 110px)", minHeight: 500, border: "none", display: "block" }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
